import { Button, Input, MagellanPopup } from 'components/atoms';
import CountrySearchCard from 'components/molecules/card/country-search/CountrySearchCard';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiStarFill, PiX } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetCountries } from 'services/enums';
import { ICountry } from 'services/enums/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface ICountrySelectPopupFormProps {
  trigger: JSX.Element | ((isOpen: boolean) => JSX.Element) | undefined;
  onSelectCountry?: (country: ICountry, actions: PopupActions) => void;
  disabledCountries?: string[]; // np. ['PL', 'DE']
  initialCountry?: ICountry | null;
}

const CountrySelectPopupForm = ({
  trigger,
  onSelectCountry,
  disabledCountries,
  initialCountry = null,
}: ICountrySelectPopupFormProps) => {
  const popupRef = useRef<PopupActions | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState<ICountry | null>(null);
  const { t, i18n } = useTranslation();

  const countries = useGetCountries(open);

  const availableCountries = useMemo(() => {
    return (countries.data ?? []).filter((item) => !disabledCountries?.includes(item.codeAlfa2));
  }, [countries.data, disabledCountries]);

  const filteredCountries = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return availableCountries.filter((item) => {
      if (!normalizedSearch) return true;

      const translatedName = t(`countries.${item.codeAlfa2}`);
      const displayName =
        translatedName !== `countries.${item.codeAlfa2}`
          ? translatedName
          : i18n.language === 'pl'
            ? item.polishName
            : item.englishName;

      return [displayName, item.polishName, item.englishName, item.codeAlfa2, item.codeAlfa3]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [availableCountries, i18n.language, search, t]);

  const suggestedCountries = useMemo(() => {
    if (search.trim().length) return [];

    const browserLocales = navigator.languages?.length ? navigator.languages : [navigator.language];

    const localeCountryCodes = browserLocales
      .map((locale) => locale.split('-')[1]?.toUpperCase())
      .filter(Boolean) as string[];

    const suggestedCodes = new Set<string>();

    if (initialCountry?.codeAlfa2) {
      suggestedCodes.add(initialCountry.codeAlfa2);
    }

    localeCountryCodes.forEach((code) => suggestedCodes.add(code));

    return availableCountries.filter((item) => suggestedCodes.has(item.codeAlfa2));
  }, [availableCountries, initialCountry, search]);

  return (
    <Popup
      ref={popupRef}
      open={open}
      onOpen={() => {
        setOpen(true);
        setCountry(initialCountry);
      }}
      onClose={(e) => {
        e?.stopPropagation();
        setOpen(false);
        setCountry(null);
        setSearch('');
      }}
      modal
      nested
      trigger={trigger}
    >
      <MagellanPopup>
        <MagellanPopup.Header onClose={() => popupRef.current?.close()}>
          {t('button.select-country')}
        </MagellanPopup.Header>

        <Input.Text
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          name=""
          appBackground
          placeholder={t('input.placeholder.search-country')}
        />

        <div className="flex flex-col w-full overflow-auto h-[450px] [scrollbar-width:none] gap-8 max-w-[350px]">
          {!search.length && suggestedCountries.length > 0 && (
            <div className="flex flex-col w-full gap-2">
              <div className="flex w-full items-center gap-2 px-8 py-4 text-base opacity-65">
                <PiStarFill opacity={0.5} />
                {t('popup.suggested-countries')}
              </div>

              {suggestedCountries.map((item) => (
                <CountrySearchCard
                  key={`suggested-${item.codeAlfa2}`}
                  country={item}
                  isSelected={item.codeAlfa2 === country?.codeAlfa2}
                  onClick={() =>
                    setCountry((prevCountry) =>
                      prevCountry?.codeAlfa2 === item.codeAlfa2 ? null : item,
                    )
                  }
                />
              ))}
            </div>
          )}

          <div className="flex flex-col w-full gap-2">
            <div className="flex px-8 py-4 text-base opacity-65">{t('popup.all-countries')}</div>

            {filteredCountries.map((item) => (
              <CountrySearchCard
                key={item.codeAlfa2}
                country={item}
                isSelected={item.codeAlfa2 === country?.codeAlfa2}
                onClick={() =>
                  setCountry((prevCountry) =>
                    prevCountry?.codeAlfa2 === item.codeAlfa2 ? null : item,
                  )
                }
              />
            ))}
          </div>
        </div>

        <div className="flex justify-between w-full">
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t('button.cancel')} />
          </Button>

          <Button
            variant={BUTTON_VARIANT.submit}
            disabled={!country}
            onClick={() =>
              country &&
              onSelectCountry &&
              popupRef.current &&
              onSelectCountry(country, popupRef.current)
            }
          >
            <Button.Text text={t('button.save')} />
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default CountrySelectPopupForm;
